/**
 * Marks our own browsers as internal traffic in GA4.
 *
 * Visiting any page with `?internal=1` flags this browser; `?internal=0`
 * clears it. While flagged, GA4 is told `traffic_type: 'internal'` before its
 * first `config`, so every event the tag sends carries it, page views
 * included. GA4's "Internal traffic" data filter excludes on exactly that
 * event parameter (GA4 Admin, Data collection and modification, Data filters).
 *
 * The flag lives in localStorage AND a first-party cookie, so clearing one of
 * them (a cookie sweep, a storage reset) does not quietly return a tester's
 * browser to the customer numbers. Either one being set counts.
 *
 * Consent: this module never loads or calls GA4 on its own. It only answers
 * "is this browser internal?", and `loadGA4` (which runs only after the
 * consent check in ConsentProvider) is what acts on the answer. Storing the
 * flag is the operator's own explicit request, not tracking of a visitor.
 *
 * Every storage access is wrapped: Safari private mode, blocked site data and
 * sandboxed previews all throw, and none of that may break a page load.
 */

export const INTERNAL_PARAM = "internal";
export const INTERNAL_STORAGE_KEY = "cch.internal-traffic";
export const INTERNAL_COOKIE = "cch_internal";
const MAX_AGE_SECONDS = 2 * 365 * 24 * 60 * 60;

function readStorage(): boolean {
  try {
    return window.localStorage.getItem(INTERNAL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function readCookie(): boolean {
  try {
    return document.cookie.split("; ").some((c) => c === `${INTERNAL_COOKIE}=1`);
  } catch {
    return false;
  }
}

export function isInternalTraffic(): boolean {
  return readStorage() || readCookie();
}

export function setInternalTraffic(internal: boolean): void {
  try {
    if (internal) window.localStorage.setItem(INTERNAL_STORAGE_KEY, "1");
    else window.localStorage.removeItem(INTERNAL_STORAGE_KEY);
  } catch {
    // Storage unavailable: the cookie below still carries the flag.
  }
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = internal
      ? `${INTERNAL_COOKIE}=1; max-age=${MAX_AGE_SECONDS}; SameSite=Lax; path=/${secure}`
      : `${INTERNAL_COOKIE}=; max-age=0; SameSite=Lax; path=/${secure}`;
  } catch {
    // Cookies blocked: localStorage above still carries the flag.
  }
}

/**
 * Reads `?internal=1|0` from the landing URL, stores or clears the flag, and
 * removes the parameter from the address bar so it never reaches GA4 as part
 * of `page_location` or gets shared in a copied link. Any other value is
 * ignored and left alone. Run once at boot, before GA4 can load.
 */
export function syncInternalTrafficFromUrl(): void {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const value = url.searchParams.get(INTERNAL_PARAM);
  if (value !== "1" && value !== "0") return;

  setInternalTraffic(value === "1");

  try {
    url.searchParams.delete(INTERNAL_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    // Leaving the parameter in the URL is harmless.
  }
}
